import { DeliveryDetailSchema, type DeliveryDetail } from '../schemas/deliveryDetail';
import { findUserInMock } from './users';

// Showcase «Entrega» ids, one per timeline scenario. Exported so the H4 list mock can
// align its row ids to these (click → detail) without magic strings. UUID-shaped, as the
// detail is read by delivery UUID (AS-026); fictional — never real «Usuario» PII.
export const deliveryDetailMockIds = {
  stored: 'd0000000-0000-4000-8000-000000000001',
  storedExtended: 'd0000000-0000-4000-8000-000000000002',
  collected: 'd0000000-0000-4000-8000-000000000003',
  expired: 'd0000000-0000-4000-8000-000000000004',
  retrieved: 'd0000000-0000-4000-8000-000000000005',
  nonCollected: 'd0000000-0000-4000-8000-000000000006',
  blocked: 'd0000000-0000-4000-8000-000000000008',
  empty: 'd0000000-0000-4000-8000-000000000007',
  // A collection the SCHEDULED CONFIRMATION closed — nobody pressed confirm (CBX-11159). It is the
  // only fixture that makes «Autoconfirmado» reachable end to end, because the panel's
  // own «Confirmar recogida» resolves the authorship to `null` (a CC-forced transition creates no
  // pickup, so the backend cannot attribute it). Reachable from the overflow user's «Entregas» list.
  collectedAutoconfirmed: 'd0000000-0000-4000-8000-000000000009',
} as const;

// The «buzón» id of each fixture — the key the box-log read is made by (contract update
// 2026-07-27: `box: {id, number}`). Exported so the locker-log mock keys its datasets on the
// same boxes without magic strings. `blocked` deliberately reuses `stored`'s box: both fixtures
// carry «Matrícula» DIZ03, so they are the same physical «buzón» and share one log. The `empty`
// fixture has no box at all (see BARE) and therefore no entry here.
export const deliveryDetailMockBoxIds = {
  stored: '59101',
  storedExtended: '59102',
  collected: '59112',
  expired: '59105',
  retrieved: '59119',
  nonCollected: '59107',
  blocked: '59101',
  collectedAutoconfirmed: '59122',
} as const;

// The **devices-service** id of each fixture's cerradura — the key the code-sequence read is made
// by (contract update 2026-08-03, CBX-10985; the cerradura itself travels INSIDE `box` since the
// 2026-08-18 nesting, CBX-10954). Exported so the codes mock keys its datasets on the same locks
// without magic strings. `stored`, `storedExtended` and `blocked` share one id on purpose: all three
// carry qr EH7JFF, so they are the same physical cerradura and its sequences are one and the same.
// `nonCollected` is **null** — a cerradura that was never migrated to devices has no counterpart
// there, so it exercises the «no codes block» branch (see `hasLockCodes`).
export const deliveryDetailMockLockIds = {
  stored: '1c000000-0000-4000-8000-000000000001',
  storedExtended: '1c000000-0000-4000-8000-000000000001',
  collected: '1c000000-0000-4000-8000-000000000002',
  expired: '1c000000-0000-4000-8000-000000000003',
  retrieved: '1c000000-0000-4000-8000-000000000004',
  nonCollected: null,
  blocked: '1c000000-0000-4000-8000-000000000001',
  collectedAutoconfirmed: '1c000000-0000-4000-8000-000000000009',
} as const;

// The `courier.id` each fixture's transportista carries inside the nested `carrier` object (contract
// update 2026-08-18, CBX-10954). Fictional, but shaped like the real master's INT FK. NOT exported
// and never read by the panel — the schema strips `carrier.id` on purpose (nothing on the detail
// renders it); the fixtures carry it only so they cannot diverge from the delivered wire, which
// means every mock read exercises that strip.
const CARRIER_IDS = {
  amazon: 4800,
  seur: 1204,
  gls: 2311,
  dhl: 907,
  correos: 145,
} as const;

// The ubicación each fixture belongs to, keyed by street so the sharing is visible: `stored` and
// `storedExtended` sit at the SAME postal address, so they must carry the SAME id or the mock would
// show two buildings where the dataset says there is one. `retrieved` is deliberately absent from
// this table — it is the fixture that keeps the no-id route reachable by hand in the running app.
const LOCATION_IDS = {
  falsa123: 4471,
  luz8: 5120,
  anochecer21: 5233,
  mayor1: 3097,
  sol20: 6014,
  alcala482: 2885,
  bare: 7788,
} as const;

// A legacy / not-yet-populated detail: no dates (empty timeline), no box and no address. Also the
// shape an unknown id falls back to, carrying the requested id so the copy button still
// shows what was clicked. No `box` means no cerradura either — the lock lives inside it since
// CBX-10954, so «sin buzón» takes it along, which is what it always meant.
const BARE = {
  status: 'stored',
  tracking: null,
  carrier: null,
  box: null,
  location: { id: LOCATION_IDS.bare, timezone: 'Europe/Madrid', address: null },
  stored_date: null,
  collected_date: null,
  blocked_date: null,
  estimated_expiration_date: null,
  is_extended_expiration_date: false,
};

const DAY_MS = 86_400_000;

// The showcase «Entrega» is the ONE fixture whose dates are relative to now; exported so the list
// mock can echo them and keep click → detail coherent. «Ampliar custodia» derives the calendar's
// floor from the expiration IN FORCE, so a hard-coded date would have the calendar opening months
// in the past within weeks of being written — the fixture would rot rather than go merely stale.
// Every other fixture keeps its absolute date on purpose: several exist precisely to BE stale (a
// restore has to land in `expired` somewhere, which is what `blocked` is for).
export function isoDaysFromNow(days: number): string {
  return new Date(Date.now() + days * DAY_MS).toISOString();
}

// Fixtures in the snake_case wire shape, parsed through the schema so the mock can never
// drift from the contract. Statuses carry the canonical lowercase wire strings the backend
// confirmed (underscores: non_collected). Keyed by delivery UUID; dates/boxes echo the H4
// list fixtures for the same scenarios so click → detail stays coherent. Nested since CBX-10954:
// the cerradura inside `box`, timezone + dirección inside `location`, and the extension policy
// inside `carrier` (where it absorbed the flat `carrier_allows_extension` of CBX-11059).
const RAW_DATASET: Record<string, unknown> = {
  [deliveryDetailMockIds.stored]: {
    id: deliveryDetailMockIds.stored,
    status: 'stored',
    tracking: '1Z2X-AMZ-77104',
    carrier: { id: CARRIER_IDS.amazon, name: 'Amazon Logistics', allows_extension: true },
    box: {
      id: deliveryDetailMockBoxIds.stored,
      number: 'DIZ03',
      lock: { id: deliveryDetailMockLockIds.stored, qr: 'EH7JFF', model: 'LOCK1' },
    },
    location: {
      id: LOCATION_IDS.falsa123,
      timezone: 'Europe/Madrid',
      address: 'C/ Falsa 123, 28080 Madrid',
    },
    stored_date: isoDaysFromNow(-3),
    collected_date: null,
    blocked_date: null,
    estimated_expiration_date: isoDaysFromNow(3),
    is_extended_expiration_date: false,
    is_return: false,
    // The showcase «Entrega» is the SHARED one, and it carries both kinds of authorized pickup:
    // one with a readable account, and one authorized to a bare phone that has none. Fictional
    // people throughout — a fixture is the last place real «Usuario» PII should ever land.
    recipient: { id: 330148, name: 'Lucía Fernández Castro', phone: '+34612348902' },
    shared_with: [
      { id: 456, name: 'Luis Gil', phone: '+34611445566' },
      { id: null, name: null, phone: '+34600112233' },
    ],
  },
  [deliveryDetailMockIds.storedExtended]: {
    id: deliveryDetailMockIds.storedExtended,
    status: 'stored',
    tracking: '1Z2X-AMZ-77105',
    carrier: { id: CARRIER_IDS.amazon, name: 'Amazon Logistics', allows_extension: true },
    box: {
      id: deliveryDetailMockBoxIds.storedExtended,
      number: 'DIZ04',
      lock: { id: deliveryDetailMockLockIds.storedExtended, qr: 'EH7JFF', model: 'LOCK1' },
    },
    location: {
      id: LOCATION_IDS.falsa123,
      timezone: 'Europe/Madrid',
      address: 'C/ Falsa 123, 28080 Madrid',
    },
    stored_date: '2026-06-02T10:00:00Z',
    collected_date: null,
    blocked_date: null,
    estimated_expiration_date: '2026-06-09T23:59:00Z',
    is_extended_expiration_date: true,
    is_return: false,
  },
  [deliveryDetailMockIds.collected]: {
    id: deliveryDetailMockIds.collected,
    status: 'collected',
    tracking: '1Z7B-SEUR-55100',
    carrier: { id: CARRIER_IDS.seur, name: 'SEUR', allows_extension: true },
    box: {
      id: deliveryDetailMockBoxIds.collected,
      number: 'DIZ12',
      lock: { id: deliveryDetailMockLockIds.collected, qr: 'JJ12KK', model: 'LOCK2' },
    },
    location: {
      id: LOCATION_IDS.luz8,
      timezone: 'Europe/Madrid',
      address: 'Av. de la Luz 8, 28020 Madrid',
    },
    stored_date: '2026-05-12T09:05:00Z',
    collected_date: '2026-05-14T17:20:00Z',
    blocked_date: null,
    estimated_expiration_date: null,
    is_extended_expiration_date: false,
    is_return: false,
    // The «Usuario» pressed confirm from their own session (`Pickup.confirm`).
    collected_by_user: true,
    // A titular and an EMPTY list, which is the majority case and not the same as «no data»: the
    // section is meant to disappear here, so a mock run has to be able to tell the two apart.
    recipient: { id: 789, name: 'Marta Ibáñez Soto', phone: '+34655221100' },
    shared_with: [],
  },
  // The autoconfirmación case: collected, but closed by the scheduled confirmation instead of by the
  // «Usuario» (`Pickup.auto_confirm`) — the one that ends in a compensation decision. Echoes the
  // overflow user's second list row (ABC02 · TRK0002 · SEUR) so click → detail stays coherent.
  [deliveryDetailMockIds.collectedAutoconfirmed]: {
    id: deliveryDetailMockIds.collectedAutoconfirmed,
    status: 'collected',
    tracking: 'TRK0002',
    carrier: { id: CARRIER_IDS.seur, name: 'SEUR', allows_extension: true },
    box: {
      id: deliveryDetailMockBoxIds.collectedAutoconfirmed,
      number: 'ABC02',
      lock: { id: deliveryDetailMockLockIds.collectedAutoconfirmed, qr: 'MM09NN', model: 'LOCK2' },
    },
    location: {
      id: LOCATION_IDS.anochecer21,
      timezone: 'Europe/Madrid',
      address: 'C/ del Anochecer 21, 28031 Madrid',
    },
    stored_date: '2026-06-18T10:00:00Z',
    collected_date: '2026-06-19T10:00:00Z',
    blocked_date: null,
    estimated_expiration_date: null,
    is_extended_expiration_date: false,
    is_return: false,
    collected_by_user: false,
  },
  [deliveryDetailMockIds.expired]: {
    id: deliveryDetailMockIds.expired,
    status: 'expired',
    tracking: '1Z5C-GLS-78119',
    carrier: { id: CARRIER_IDS.gls, name: 'GLS', allows_extension: true },
    box: {
      id: deliveryDetailMockBoxIds.expired,
      number: 'DIZ05',
      lock: { id: deliveryDetailMockLockIds.expired, qr: 'MN34OP', model: 'LOCK1' },
    },
    location: {
      id: LOCATION_IDS.mayor1,
      timezone: 'Europe/Madrid',
      address: 'C/ Mayor 1, 28013 Madrid',
    },
    stored_date: '2026-05-04T20:45:00Z',
    collected_date: null,
    blocked_date: null,
    estimated_expiration_date: null,
    is_extended_expiration_date: false,
    is_return: false,
  },
  [deliveryDetailMockIds.retrieved]: {
    id: deliveryDetailMockIds.retrieved,
    status: 'retrieved',
    tracking: '1Z21-DHL-54007',
    carrier: { id: CARRIER_IDS.dhl, name: 'DHL', allows_extension: true },
    box: {
      id: deliveryDetailMockBoxIds.retrieved,
      number: 'LDN19',
      lock: { id: deliveryDetailMockLockIds.retrieved, qr: 'QR56ST', model: null },
    },
    // No `id` on purpose: the one fixture that keeps «backend has not published the field yet»
    // reachable by hand in the running app, where the «Ver entregas» control must not appear.
    location: { timezone: 'Europe/London', address: '221B Baker Street, London NW1' },
    stored_date: '2026-04-28T11:10:00Z',
    collected_date: null,
    blocked_date: null,
    retrieved_date: '2026-05-08T09:00:00Z',
    estimated_expiration_date: null,
    is_extended_expiration_date: false,
    is_return: false,
  },
  [deliveryDetailMockIds.nonCollected]: {
    id: deliveryDetailMockIds.nonCollected,
    status: 'non_collected',
    tracking: '1Z9D-CORREOS-30012',
    carrier: { id: CARRIER_IDS.correos, name: 'Correos', allows_extension: true },
    box: {
      id: deliveryDetailMockBoxIds.nonCollected,
      number: 'DIZ07',
      lock: { id: deliveryDetailMockLockIds.nonCollected, qr: 'UV78WX', model: 'LOCK2' },
    },
    location: {
      id: LOCATION_IDS.sol20,
      timezone: 'Europe/Madrid',
      address: 'C/ del Sol 20, 28045 Madrid',
    },
    stored_date: '2026-04-19T07:55:00Z',
    collected_date: null,
    blocked_date: null,
    non_collected_date: '2026-04-23T09:00:00Z',
    estimated_expiration_date: null,
    is_extended_expiration_date: false,
    is_return: false,
  },
  [deliveryDetailMockIds.blocked]: {
    id: deliveryDetailMockIds.blocked,
    status: 'blocked',
    tracking: '1Z2X-AMZ-77104',
    carrier: { id: CARRIER_IDS.amazon, name: 'Amazon Logistics', allows_extension: true },
    box: {
      id: deliveryDetailMockBoxIds.blocked,
      number: 'DIZ03',
      lock: { id: deliveryDetailMockLockIds.blocked, qr: 'EH7JFF', model: 'LOCK1' },
    },
    location: {
      id: LOCATION_IDS.alcala482,
      timezone: 'Europe/Madrid',
      address: 'C/ Alcalá 482, 28027 Madrid',
    },
    stored_date: '2026-06-03T18:31:00Z',
    collected_date: null,
    blocked_date: '2026-06-05T09:12:00Z',
    estimated_expiration_date: '2026-06-06T23:59:00Z',
    is_extended_expiration_date: false,
    is_return: false,
  },
  [deliveryDetailMockIds.empty]: { ...BARE, id: deliveryDetailMockIds.empty },
};

// A dev-only overlay of deliveries confirmed as collected this session. The base RAW_DATASET
// stays immutable; only this overlay mutates, so a confirm-collect can be exercised end to end
// (confirm → refetch shows Collected) without editing the fixtures. Tests clear it between cases.
const collectedOverlay = new Map<string, string>();

// Flip a delivery to collected in the mock, stamping a fresh collected date — the wire field the
// «Recogido» timeline row is built from. Idempotent: re-confirming just restamps.
export function markDeliveryCollectedInMock(id: string): void {
  collectedOverlay.set(id, new Date().toISOString());
}

// A dev-only overlay of deliveries restored this session (the «Restaurar entrega» WRITE). Like the
// collected overlay the base RAW_DATASET stays immutable; the restore recomputes the landing state
// and stores the wire deltas here, so restore → refetch surfaces the new collectable state end to
// end. Tests clear it between cases.
interface RestoreDelta {
  status: 'stored' | 'expired';
  estimated_expiration_date: string | null;
  collected_date: string | null;
}
const restoreOverlay = new Map<string, RestoreDelta>();

// A restore lands the delivery in its date-consistent collectable state: Stored while the
// collection window is open, Expired once the expiration date has passed. The fixtures carry
// absolute past dates, so the landing is recomputed relative to now — a delivery whose estimate is
// still open (or that has no recorded window, e.g. one previously collected) reopens as Stored with
// a fresh future estimate; one whose estimate already passed lands Expired. A restore supersedes any
// prior collected overlay for the id, but KEEPS the last pickup date (CBX-10877: the backend now
// conserves collected_date across a restore instead of clearing it) — the detail then renders by
// status, so the reopened parcel shows as stored/expired, never «Recogido».
export function markDeliveryRestoredInMock(id: string): void {
  const base = (RAW_DATASET[id] ?? { ...BARE, id }) as Record<string, unknown>;
  const estimate = (base.estimated_expiration_date as string | null) ?? null;
  const alreadyExpired = estimate !== null && Date.parse(estimate) <= Date.now();
  // The last pickup date to preserve: one confirmed this session (collected overlay) wins over the
  // fixture's own collected_date.
  const lastCollected = collectedOverlay.get(id) ?? ((base.collected_date as string | null) ?? null);
  restoreOverlay.set(
    id,
    alreadyExpired
      ? { status: 'expired', estimated_expiration_date: estimate, collected_date: lastCollected }
      : {
          status: 'stored',
          estimated_expiration_date: estimate ?? freshExpirationEstimate(),
          collected_date: lastCollected,
        },
  );
  collectedOverlay.delete(id);
  // A restore RECOMPUTES the collection window, so it supersedes any extension granted earlier.
  // Deliberately asymmetric with non-collect and reject, which leave the expiration untouched and
  // therefore let an extension survive.
  custodyExtendedOverlay.delete(id);
}

// A collection window a few days out, so a reopened «Stored» delivery renders a coherent future
// expiration rather than a stale fixture date.
function freshExpirationEstimate(): string {
  return new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString();
}

// A dev-only overlay of deliveries confirmed as empty-pickup this session (the «Confirmar recogida
// vacía» WRITE). Like the other overlays the base RAW_DATASET stays immutable; membership flips the
// delivery to non_collected on the next fetch, so non-collect → refetch surfaces the empty-pickup
// state end to end. Tests clear it between cases.
const nonCollectedOverlay = new Set<string>();

// Flip a delivery to non_collected in the mock. Supersedes any prior collected overlay for the id
// (the parcel was found not collected), and clears the collected date via the fetch branch below.
// Idempotent: re-confirming just re-adds the id.
export function markDeliveryNonCollectedInMock(id: string): void {
  nonCollectedOverlay.add(id);
  collectedOverlay.delete(id);
}

// A dev-only overlay of deliveries rejected this session (the «Rechazar recogida» WRITE). Like the
// other overlays the base RAW_DATASET stays immutable; membership flips the delivery to rejected on
// the next fetch, so reject → refetch surfaces the rejection end to end. Tests clear it between cases.
const rejectedOverlay = new Set<string>();

// Flip a delivery to rejected in the mock. Supersedes any prior collected overlay for the id — the
// `collected → rejected` correction: opening the «buzón» auto-registered a pickup the «Usuario» then
// declined — and clears the collected date via the fetch branch below. Idempotent.
export function markDeliveryRejectedInMock(id: string): void {
  rejectedOverlay.add(id);
  collectedOverlay.delete(id);
}

// A dev-only overlay of deliveries retrieved this session (the «Recuperado» WRITE). Like the other
// overlays the base RAW_DATASET stays immutable; membership flips the delivery to retrieved on the
// next fetch, so retrieve → refetch surfaces the courier retrieval end to end. Tests clear it
// between cases.
const retrievedOverlay = new Set<string>();

// A fixed fiction in the fixtures' own timeframe: the wire stamps the real instant, the mock only
// needs the «Recuperado por el mensajero» row to render dated.
const RETRIEVED_AT = '2026-05-09T10:00:00Z';

// Flip a delivery to retrieved in the mock — the courier took the parcel. Supersedes prior
// rejected/collected/non-retrieved overlays for the id (a retrieval closes the case, and a real
// retrieval may follow an empty one). Idempotent.
export function markDeliveryRetrievedInMock(id: string): void {
  retrievedOverlay.add(id);
  rejectedOverlay.delete(id);
  collectedOverlay.delete(id);
  nonRetrievedOverlay.delete(id);
}

// A dev-only overlay of deliveries the courier found empty this session (the «Recuperación vacía»
// WRITE). Like the other overlays the base RAW_DATASET stays immutable; membership flips the delivery
// to non_retrieved on the next fetch, so empty-retrieval → refetch surfaces the forced transition end
// to end. Tests clear it between cases.
const nonRetrievedOverlay = new Set<string>();

// A fixed fiction in the fixtures' own timeframe: the wire stamps the real instant, the mock only
// needs the «Recuperación vacía» row to render dated.
const NON_RETRIEVED_AT = '2026-05-09T11:00:00Z';

// Flip a delivery to non_retrieved in the mock — the courier opened the «buzón» and it was empty.
// Supersedes prior rejected/collected overlays (rejected → non_retrieved is a real flow). The guard
// is the ONE place the asymmetry with a retrieval lives: `retrieved → non_retrieved` was cut by
// product decision for legacy parity, so an existing retrieval wins and this write is a no-op — while
// the opposite direction is allowed, and is handled by markDeliveryRetrievedInMock above. Because the
// guard refuses the id, the two overlays are mutually exclusive by construction. Idempotent.
export function markDeliveryNonRetrievedInMock(id: string): void {
  if (retrievedOverlay.has(id)) return;
  nonRetrievedOverlay.add(id);
  rejectedOverlay.delete(id);
  collectedOverlay.delete(id);
}

// A dev/test hook: when set, the next enableDeliveryMock call rejects with this backend error code
// (simulating a 409), so the case-specific error banners are exercisable. Persists until reset.
let injectedEnableDeliveryError: string | null = null;
export function setEnableDeliveryMockError(code: string | null): void {
  injectedEnableDeliveryError = code;
}
export function getEnableDeliveryMockError(): string | null {
  return injectedEnableDeliveryError;
}

// The non-collect twin of the enable hook: when set, the next nonCollectDeliveryMock call rejects
// with this backend error code. The front ignores the code for this action (generic banner), but the
// hook keeps dev/test parity with the sibling WRITEs. Persists until reset.
let injectedNonCollectError: string | null = null;
export function setNonCollectDeliveryMockError(code: string | null): void {
  injectedNonCollectError = code;
}
export function getNonCollectDeliveryMockError(): string | null {
  return injectedNonCollectError;
}

// The reject twin of the enable/non-collect hooks: when set, the next rejectDeliveryMock call rejects
// with this backend error code. The front ignores the code for this action (generic banner), but the
// hook keeps dev/test parity with the sibling WRITEs. Persists until reset.
let injectedRejectError: string | null = null;
export function setRejectDeliveryMockError(code: string | null): void {
  injectedRejectError = code;
}
export function getRejectDeliveryMockError(): string | null {
  return injectedRejectError;
}

// A dev-only overlay of deliveries whose custody was extended this session (the «Ampliar custodia»
// WRITE). Like the other overlays the base RAW_DATASET stays immutable. Values are ISO instants.
const custodyExtendedOverlay = new Map<string, string>();

// Apply the chosen day as the delivery's new estimated expiration and flip the «extended» flag —
// exactly the two things the endpoint is asked to do, so this mock encodes no rule of its own.
// ⚠️ MOCK FICTION, and the only one left: the wire body is date-only while the detail renders an
// INSTANT, so the mock pins the day at 23:59 in the «buzón»'s timezone. A mock-vs-real divergence
// in the TIME OF DAY is therefore an expected finding, not a bug. Precedent:
// markDeliveryRestoredInMock.
export function markDeliveryCustodyExtendedInMock(id: string, expirationDate: string): void {
  const base = (RAW_DATASET[id] ?? { ...BARE, id }) as Record<string, unknown>;
  // The zone moved into `location` with CBX-10954; the fallback stays for the same reason it always
  // did — an unknown id lands on BARE, which is built here rather than read from the dataset.
  const timeZone =
    (base.location as { timezone?: string } | undefined)?.timezone ?? 'Europe/Madrid';
  custodyExtendedOverlay.set(id, endOfDayInTimeZone(expirationDate, timeZone));
}

// `YYYY-MM-DD` + 23:59 local to `timeZone`, as an ISO instant. Derived by probing the zone's offset
// at that wall-clock moment (Intl is the repo's only timezone tool — no date library down here).
// h23 explicitly: with a bare `hour12: false` some engines render midnight as hour 24, which would
// shift the probe by a full day.
function endOfDayInTimeZone(day: string, timeZone: string): string {
  const naive = Date.parse(`${day}T23:59:00Z`);
  const probe = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(naive));
  const at = (type: string) => Number(probe.find((part) => part.type === type)?.value ?? '0');
  const shown = Date.UTC(at('year'), at('month') - 1, at('day'), at('hour'), at('minute'));
  return new Date(naive - (shown - naive)).toISOString();
}

// The extend twin of the enable/non-collect/reject hooks: when set, the next extendCustodyMock call
// rejects with this backend error code, so the per-code banners are exercisable. Persists until reset.
let injectedExtendCustodyError: string | null = null;
export function setExtendCustodyMockError(code: string | null): void {
  injectedExtendCustodyError = code;
}
export function getExtendCustodyMockError(): string | null {
  return injectedExtendCustodyError;
}

// The titular a «Cambiar titular» left behind, in the wire's own shape. Its PRESENCE also carries
// the second half of the delivered behaviour — every share of the «Entrega» revoked — because the
// two happen in one transaction and no fixture needs them apart.
const recipientOverlay = new Map<string, { id: number; name: string | null; phone: string | null }>();

// Apply the reassignment: the «Entrega» is handed to `userId` and its authorized pickups are gone.
// The name and phone are read from the ACCOUNTS mock, which is the only reason this module knows
// that one exists — the wire carries an id and nothing else, so without the lookup the card could
// never show who the parcel went to, which is the whole point of exercising this flow. Same
// argument, same precedent, as `markUserUpdatedInMock` reaching for a location to imitate the
// matrícula it does not send. An id the accounts mock does not know lands `{name: null, phone:
// null}`: «the account exists and its row could not be read», the honest fallback and a real
// semantic of the contract ([AS-039]).
export function markDeliveryRecipientChangedInMock(id: string, userId: number): void {
  const account = findUserInMock(userId);
  recipientOverlay.set(id, {
    id: userId,
    name: account?.name ?? null,
    phone: account?.phone ?? null,
  });
}

// Who holds the «Entrega» NOW — the overlay first, so a second reassignment in the same dev session
// compares against the titular the first one left, exactly as the real compare-and-set would.
export function currentRecipientIdInMock(id: string): number | null {
  const changed = recipientOverlay.get(id);
  if (changed !== undefined) return changed.id;
  const base = (RAW_DATASET[id] ?? { ...BARE, id }) as Record<string, unknown>;
  return (base.recipient as { id?: number } | undefined)?.id ?? null;
}

let injectedChangeRecipientError: string | null = null;
export function setChangeRecipientMockError(code: string | null): void {
  injectedChangeRecipientError = code;
}
export function getChangeRecipientMockError(): string | null {
  return injectedChangeRecipientError;
}

// Clear the overlays and any injected error so the shared module state does not leak between tests.
export function resetDeliveryDetailMock(): void {
  recipientOverlay.clear();
  injectedChangeRecipientError = null;
  collectedOverlay.clear();
  restoreOverlay.clear();
  nonCollectedOverlay.clear();
  rejectedOverlay.clear();
  retrievedOverlay.clear();
  nonRetrievedOverlay.clear();
  custodyExtendedOverlay.clear();
  injectedEnableDeliveryError = null;
  injectedNonCollectError = null;
  injectedRejectError = null;
  injectedExtendCustodyError = null;
}

// The STATUS overlays — mutually exclusive, each deciding `status` and `collected_date`, in the
// order that settles which one wins. Extracted from the read so a WRITE mock can ask what state a
// delivery is in NOW without a second copy of this precedence: the eligibility guard of «Cambiar
// titular» has to see the same `stored` a dev walked a fixture into through «Habilitar recogida»,
// not the state that fixture was written with.
function withStatusOverlays(id: string, base: Record<string, unknown>): Record<string, unknown> {
  let wire: Record<string, unknown>;

  // A retrieval closes the case: the courier took the parcel, so it wins over every earlier status
  // overlay — including a restore that reopened the pickup as stored, which is retrievable — and
  // voids any collected date (the parcel demonstrably never left with the «Usuario»).
  const restored = restoreOverlay.get(id);
  if (retrievedOverlay.has(id)) {
    wire = { ...base, status: 'retrieved', collected_date: null, retrieved_date: RETRIEVED_AT };
  } else if (nonRetrievedOverlay.has(id)) {
    // The courier opened the «buzón» and found it empty: terminal, no collected date, dated by the
    // overlay's fiction. It sits HERE for the restore below — an empty retrieval covers a parcel a
    // prior restore had reopened. Its position relative to the retrieval above is not a tie-break:
    // markDeliveryNonRetrievedInMock's guard keeps the two overlays mutually exclusive.
    wire = {
      ...base,
      status: 'non_retrieved',
      collected_date: null,
      non_retrieved_date: NON_RETRIEVED_AT,
    };
  } else if (restored !== undefined) {
    wire = {
      ...base,
      status: restored.status,
      collected_date: restored.collected_date,
      estimated_expiration_date: restored.estimated_expiration_date,
    };
  } else if (nonCollectedOverlay.has(id)) {
    // A non-collect override lands the delivery in non_collected with no collected date.
    wire = { ...base, status: 'non_collected', collected_date: null };
  } else if (rejectedOverlay.has(id)) {
    // A reject override lands the delivery in rejected with no collected date (any auto-registered
    // pickup is voided by the correction).
    wire = { ...base, status: 'rejected', collected_date: null };
  } else {
    const collectedDate = collectedOverlay.get(id);
    // The authorship is pinned to `null`, never left to the base fixture: backend cannot attribute a
    // Customer-Care-forced transition (it creates no pickup, and the authorship lives on the pickup),
    // so this is the honest dev-time image of the panel's own «Confirmar recogida». Setting it
    // explicitly matters because this branch SPREADS the base — omitting the key would let the
    // `collected` fixture's own `true` survive a forced collection and label it as confirmed by the
    // «Usuario». Reaching `false` takes the autoconfirmed fixture, not this write.
    wire =
      collectedDate !== undefined
        ? { ...base, status: 'collected', collected_date: collectedDate, collected_by_user: null }
        : base;
  }

  return wire;
}

// The state a delivery is in once the overlays are applied — the read's own answer, so a write
// guard and the screen can never disagree about it.
export function currentStatusInMock(id: string): string {
  const base = (RAW_DATASET[id] ?? { ...BARE, id }) as Record<string, unknown>;
  const status = withStatusOverlays(id, base).status;
  // Narrowed rather than coerced: the wire shape here is `unknown`, and a fixture whose status is
  // not a string is a broken fixture — `''` matches no state, so the guard reading this stays
  // fail-closed instead of reassigning on an `[object Object]`.
  return typeof status === 'string' ? status : '';
}

export async function fetchDeliveryDetailMock(id: string): Promise<DeliveryDetail> {
  // Simulate network latency for a realistic dev experience.
  await new Promise((resolve) => setTimeout(resolve, 100));
  const base = (RAW_DATASET[id] ?? { ...BARE, id }) as Record<string, unknown>;
  let wire = withStatusOverlays(id, base);

  // …then LAYER the custody extension on top, because it is orthogonal to status: it moves a
  // deadline and never transitions the delivery, so it must COMPOSE with whichever status overlay
  // won rather than race it for the return. Returning early instead would make the extension
  // invisible after any other write in the same session — and «Habilitar recogida» is precisely how
  // a dev walks a non-stored fixture into `stored`, the only extendable state.
  const extendedTo = custodyExtendedOverlay.get(id);
  if (extendedTo !== undefined) {
    wire = { ...wire, estimated_expiration_date: extendedTo, is_extended_expiration_date: true };
  }

  // …and the reassignment last, because it is orthogonal to both: it moves WHO the parcel is for
  // and never its state or its deadline. Revoking the shares here rather than in the overlay keeps
  // the two effects of one transaction inseparable — a fixture cannot end up reassigned with its
  // authorized pickups still listed, which is the very thing the confirm step promises will not
  // happen.
  const changedTo = recipientOverlay.get(id);
  if (changedTo !== undefined) {
    wire = { ...wire, recipient: changedTo, shared_with: [] };
  }

  return DeliveryDetailSchema.parse(wire);
}
